import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [language, setLanguage] = useState(localStorage.getItem("vlg_lang") || "en");
    const [theme, setTheme] = useState(localStorage.getItem("vlg_theme") || "dark");

    useEffect(() => {
        document.documentElement.classList.toggle("dark", theme === "dark");
        localStorage.setItem("vlg_theme", theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem("vlg_lang", language);
    }, [language]);

    const checkAuth = useCallback(async () => {
        // Skip check if returning from OAuth callback
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        try {
            const res = await api.get("/auth/me");
            setUser(res.data);

            // Sync direction: user's LOCAL choice always wins.
            // If localStorage has a value that differs from server, push local → server.
            // Only pull from server if there's no local preference at all.
            const localLang = localStorage.getItem("vlg_lang");
            const localTheme = localStorage.getItem("vlg_theme");

            const partial = {};
            if (localLang && res.data.language !== localLang) partial.language = localLang;
            if (localTheme && res.data.theme !== localTheme) partial.theme = localTheme;

            if (Object.keys(partial).length > 0) {
                try {
                    const updated = await api.patch("/auth/settings", partial);
                    setUser(updated.data);
                } catch (e) {
                    console.warn("checkAuth: settings sync failed", e);
                }
            } else {
                if (!localLang && res.data.language) setLanguage(res.data.language);
                if (!localTheme && res.data.theme) setTheme(res.data.theme);
            }
        } catch (e) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.warn("logout: server logout failed, clearing locally anyway", e);
        }
        setUser(null);
        window.location.href = "/";
    };

    const updateSettings = async (partial) => {
        if (partial.language) setLanguage(partial.language);
        if (partial.theme) setTheme(partial.theme);
        if (user) {
            try {
                const res = await api.patch("/auth/settings", partial);
                setUser(res.data);
            } catch (e) {
                console.warn("updateSettings: patch failed", e);
            }
        }
    };

    return (
        <AppContext.Provider value={{
            user, setUser, loading, checkAuth, logout,
            language, setLanguage, theme, setTheme, updateSettings,
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => useContext(AppContext);
